<script lang="ts">
    import type {IdName} from '$lib/models/types';
    import {AliiOCRTypes, ClientStatuses} from '$lib/models/client/types';
    import {createClientStore} from '$lib/stores/createClientStore';
    import {clientStore} from '$lib/stores/clientStore';
    import {mailOcrEnabled} from '$lib/stores/settingsStore';
    import {onMount} from 'svelte';
    import {XCircle} from 'phosphor-svelte';
    import {
        Autocomplete,
        type AutocompleteOption,
        popup,
        type PopupSettings,
        SlideToggle
    } from '@skeletonlabs/skeleton';

    export let exportTypes: IdName[];
  export let connectionTypes: IdName[];

  const request = createClientStore.request;

  let clientLocations: IdName[] = [];

  onMount(async () => {
    clientLocations = await clientStore.getLocations();
  });

  let newDomain: string = '';

  // Organisation — false = New (default), true = Existing
  let isExistingOrg: boolean = false;
  let orgSearchInput: string = '';
  let orgOptions: AutocompleteOption<string>[] = [];
  let orgSearchTimeout: number | undefined;

  const orgPopupSettings: PopupSettings = {
    event: 'focus-click',
    target: 'org-create-search',
    placement: 'bottom',
  };

  function onDomainAdd(): void {
    createClientStore.addDomain(newDomain);
    newDomain = '';
  }

  function onOrgModeToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    createClientStore.setOrganisationMode(!checked);
    orgSearchInput = '';
    orgOptions = [];
  }

  function onOrgSelection(event: CustomEvent): void {
    const detail = event.detail as AutocompleteOption<string>;
    orgSearchInput = detail.label;
    createClientStore.setOrganisationId(detail.value, detail.label);
  }

  function onOrgSearchInput(): void {
    clearTimeout(orgSearchTimeout);
    orgSearchTimeout = window.setTimeout(async () => {
      try {
        const results = await clientStore.searchOrganisations(orgSearchInput);
        orgOptions = results.map((x) => ({ value: x.id as string, label: x.name }));
      } catch {}
    }, 400);
  }
</script>

<form id="clientDetailsForm" class="grid h-full grid-cols-1 gap-5 lg:grid-cols-2">
  <div class="h-full border border-surface-500 p-5 rounded-container-token">
    <label class="label mb-5">
      <span>Client Name *</span>
      <input class="input" type="text" bind:value={$request.clientName} required />
    </label>
    <label class="label mb-5">
      <span>Client Status</span>
      <select class="select" bind:value={$request.clientStatus}>
        {#each ClientStatuses as status}
          <option value={status.id} selected={status.id === $request.clientStatus}>{status.name}</option>
        {/each}
      </select>
    </label>
    <label class="label mb-5">
      <span>Alii Connect Type</span>
      <select class="select" bind:value={$request.connectionTypeId}>
        {#each connectionTypes as connectionType}
          <option value={connectionType.id} selected={connectionType.id === $request.connectionTypeId}>{connectionType.name}</option>
        {/each}
      </select>
    </label>
    <label class="label mb-5">
      <span>Alii Export Type</span>
      <select class="select" bind:value={$request.exportTypeId}>
        {#each exportTypes as exportType}
          <option value={exportType.id} selected={exportType.id === $request.exportTypeId}>{exportType.name}</option>
        {/each}
      </select>
    </label>
    <label class="label mb-5">
      <span>OCR Type</span>
      <select class="select" bind:value={$request.aliiOCRType} disabled={!$mailOcrEnabled}>
        {#each AliiOCRTypes as ocrType}
          <option value={ocrType.id} selected={ocrType.id === $request.aliiOCRType}>{ocrType.name}</option>
        {/each}
      </select>
    </label>
    <label class="label mb-5">
      <span>Location</span>
      <select class="select" bind:value={$request.location}>
        {#each clientLocations as clientLocation}
          <option value={clientLocation.name} selected={clientLocation.name === $request.location}>{clientLocation.name}</option>
        {/each}
      </select>
    </label>
    <div class="label mb-5">
      <span>Organisation</span>
      <div class="mt-2 flex gap-3">
        <!-- New organisation name (left) -->
        <div class="flex flex-1 flex-col gap-1">
          <span class="text-xs text-surface-400">New</span>
          <input
            class="input"
            type="text"
            bind:value={$request.organisationName}
            maxlength="255"
            placeholder="Enter organisation name..."
            disabled={isExistingOrg}
          />
        </div>
        <!-- Toggle (centre) -->
        <div class="flex self-end items-center pb-1.5">
          <SlideToggle
            name="orgMode"
            size="sm"
            bind:checked={isExistingOrg}
            active="bg-success-500"
            background="bg-secondary-600"
            on:change={onOrgModeToggle}
            disabled
          />
        </div>
        <!-- Existing organisation search (right) -->
        <div class="flex flex-1 flex-col gap-1 relative">
          <span class="text-xs text-surface-400">Existing</span>
          <input
            class="input"
            type="search"
            bind:value={orgSearchInput}
            placeholder="Enterprise coming soon..."
            on:input={onOrgSearchInput}
            use:popup={orgPopupSettings}
            autocomplete="off"
            aria-autocomplete="none"
            disabled={!isExistingOrg}
          />
          <div data-popup="org-create-search" class="card w-full z-10 max-h-48 overflow-y-auto p-4" tabindex="-1">
            <Autocomplete bind:input={orgSearchInput} options={orgOptions} on:selection={onOrgSelection} />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="border border-surface-500 p-5 rounded-container-token">
    <div class="label">Domains *</div>
    <div class="mb-5 h-96 overflow-y-scroll rounded-md border bg-surface-500 p-3">
      {#each $request.domains as domain, index}
        <div class="mb-2 inline-flex w-full items-center justify-between rounded-md border bg-white p-5">
          <div class="overflow-hidden overflow-ellipsis text-nowrap">
            <div>{domain}</div>
          </div>
          <div class="inline-flex gap-2">
            <button class="hover:text-error-500" on:click={() => createClientStore.removeDomain(index)}>
              <XCircle></XCircle>
            </button>
          </div>
        </div>
      {/each}
    </div>
    <div class="flex w-full place-content-center gap-5">
      <input class="input" bind:value={newDomain} />
      <button class="variant-filled-success btn place-self-center" on:click={onDomainAdd} disabled={!newDomain.trim()}> Add New Domain </button>
    </div>
  </div>
</form>
